import React, { useEffect, useMemo, useState } from 'react';
import {
    Button,
    Dialog,
    DialogContent,
    DialogActions,
    Typography,
    IconButton,
    Card,
    CardContent,
    Box,
    Divider,
    LinearProgress,
    TextField
} from '@material-ui/core';
import { Cancel, NavigateBefore, NavigateNext, Send } from '@material-ui/icons';
import AudioModal from '../AudioModal';
import { makeStyles } from "@material-ui/core/styles";

const useStyles = makeStyles((theme) => ({
    modal: {
        backgroundColor: theme.palette.background.paper,
    },
    dialogPaper: {
        borderRadius: 12,
        overflow: 'hidden',
    },
    card: {
        borderRadius: 12,
        boxShadow: 'none',
        backgroundColor: 'transparent',
    },
    mediaContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '400px',
        backgroundColor: theme.palette.grey[100],
        position: 'relative',
        overflow: 'hidden',
    },
    mediaContent: {
        maxWidth: '100%',
        maxHeight: '100%',
        objectFit: 'contain',
    },
    fileName: {
        padding: theme.spacing(1, 2),
        backgroundColor: theme.palette.background.paper,
        textAlign: 'center',
        fontWeight: 500,
    },
    captionInput: {
        padding: theme.spacing(0.75, 1.5),
        '& .MuiOutlinedInput-root': {
            borderRadius: 12,
            backgroundColor: theme.palette.background.paper,
        },
        '& .MuiOutlinedInput-input': {
            padding: theme.spacing(0.75, 1.5),
            fontSize: '0.9rem',
        },
    },
    actions: {
        padding: theme.spacing(0.5, 1.5),
        backgroundColor: theme.palette.background.paper,
        borderTop: `1px solid ${theme.palette.divider}`,
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
    captionWrapper: {
        position: 'relative',
        flex: 1,
        minWidth: 0,
    },
    navigationButton: {
        color: theme.palette.text.secondary,
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
    disabledButton: {
        color: theme.palette.action.disabled,
    },
    activeButton: {
        color: theme.palette.primary.main,
    },
    listaMencao: {
        position: 'absolute',
        bottom: '100%',
        left: 0,
        right: 0,
        marginBottom: 4,
        maxHeight: 190,
        overflowY: 'auto',
        background: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 8,
        boxShadow: theme.shadows[3],
        zIndex: 20,
    },
    itemMencao: {
        padding: theme.spacing(0.75, 1.5),
        cursor: 'pointer',
    },
    itemMencaoAtivo: {
        padding: theme.spacing(0.75, 1.5),
        cursor: 'pointer',
        background: theme.palette.action.selected,
    },
    paginationText: {
        margin: theme.spacing(0, 1),
        color: theme.palette.text.secondary,
    },
}));

const MessageUploadMedias = ({ isOpen, files, onClose, onSend, onCancelSelection, mentionList = [] }) => {
    const classes = useStyles();
    const [currentIndex, setCurrentIndex] = useState(0);
    const [captions, setCaptions] = useState(files.map(() => ''));
    // ⚠️ listinha do @ (09/09/2026): uma lista de marcados por arquivo, igual as legendas.
    const [mencoes, setMencoes] = useState(files.map(() => []));
    const [buscaMencao, setBuscaMencao] = useState('');
    const [mostrarLista, setMostrarLista] = useState(false);
    const [posArroba, setPosArroba] = useState(-1);
    const [indiceSel, setIndiceSel] = useState(0);
    const [componentMounted, setComponentMounted] = useState(false);
    const [isTyping, setIsTyping] = useState(false);
    const [firstTyping, setFirstTyping] = useState(false);

    useEffect(() => {
        setFirstTyping(true);
        setComponentMounted(true);
    }, []);

    const handleClose = () => {
        onClose();
    };

    // ⚠️ QA 09/09: ao trocar de arquivo, fecha a listinha e esquece a posicao do @.
    // Sem isto ela seguia aberta e, ao escolher, reescrevia a legenda da OUTRA foto,
    // comendo um pedaco do texto.
    const fecharListaMencao = () => {
        setMostrarLista(false);
        setBuscaMencao('');
        setPosArroba(-1);
        setIndiceSel(0);
    };

    const handlePrev = () => {
        if (currentIndex > 0) {
            setFirstTyping(true);
            fecharListaMencao();
            setCurrentIndex(currentIndex - 1);
        }
    };

    const handleNext = () => {
        if (currentIndex < files.length - 1) {
            setFirstTyping(true);
            fecharListaMencao();
            setCurrentIndex(currentIndex + 1);
        }
    };

    const handleTextFieldBlur = () => {
        setIsTyping(false);
    };

    const handleCaptionChange = (e) => {
        const value = e.target.value;
        setCaptions((prevCaptions) => {
            const updatedCaptions = [...prevCaptions];
            updatedCaptions[currentIndex] = value;
            return updatedCaptions;
        });
        if (firstTyping) {
            setIsTyping(true);
        }
        // ⚠️ listinha do @: procura um @ antes do cursor e abre a lista de participantes.
        // Fecha se ja houver espaco no meio do que foi digitado (ai nao e mais um nome sendo
        // escrito) ou se a conversa nao for grupo (mentionList vem vazia).
        const cursor = typeof e.target.selectionStart === 'number' ? e.target.selectionStart : value.length;
        const antesDoCursor = value.slice(0, cursor);
        const pos = antesDoCursor.lastIndexOf('@');
        const termo = pos >= 0 ? antesDoCursor.slice(pos + 1) : '';
        if (pos >= 0 && (mentionList || []).length > 0 && termo.length <= 30 && !/\s\s/.test(termo)) {
            setPosArroba(pos);
            setBuscaMencao(termo);
            setIndiceSel(0);
            setMostrarLista(true);
        } else {
            setMostrarLista(false);
        }
    };

    // participantes que casam com o que foi digitado depois do @ (no maximo 6 na tela)
    const listaFiltrada = mostrarLista
        ? (mentionList || [])
              .filter((p) =>
                  String(p.name || '').toLowerCase().includes(String(buscaMencao || '').toLowerCase())
              )
              .slice(0, 6)
        : [];

    const escolherMencao = (participante) => {
        if (!participante || posArroba < 0) return;
        const texto = captions[currentIndex] || '';
        // ⚠️ QA 09/09: se a posicao guardada nao existe mais neste texto (troca de
        // arquivo, texto apagado), nao mexe em nada - so fecha a lista.
        if (posArroba > texto.length || texto.charAt(posArroba) !== '@') {
            setMostrarLista(false);
            setPosArroba(-1);
            return;
        }
        const antes = texto.slice(0, posArroba);
        const depois = texto.slice(posArroba + 1 + (buscaMencao || '').length);
        setCaptions((anteriores) => {
            const novas = [...anteriores];
            novas[currentIndex] = `${antes}@${participante.name} ${depois}`;
            return novas;
        });
        setMencoes((anteriores) => {
            const novas = anteriores.map((lista) => [...(lista || [])]);
            const atual = novas[currentIndex] || [];
            if (!atual.some((m) => m.jid === participante.jid)) {
                atual.push({ jid: participante.jid, name: participante.name });
            }
            novas[currentIndex] = atual;
            return novas;
        });
        setMostrarLista(false);
        setBuscaMencao('');
        setPosArroba(-1);
    };

    const handleSend = () => {
        const selectedMedias = files.map((file, index) => ({
            file,
            caption: captions[index],
            // so vai quem foi realmente escolhido na lista E cujo nome continua na legenda
            mentions: (mencoes[index] || []).filter((m) =>
                String(captions[index] || '').includes(`@${m.name}`)
            ),
        }));
        onSend(selectedMedias);
        handleClose();
    };

    const renderFileContent = useMemo(() => {
        if (!componentMounted) {
            return null;
        }
        if (firstTyping) {
            const currentFile = files[currentIndex];
            
            if (currentFile.type.startsWith('image')) {
                return (
                    <>
                        <Box className={classes.mediaContainer}>
                            <img
                                alt={`Imagem ${currentIndex + 1}`}
                                src={URL.createObjectURL(currentFile)}
                                className={classes.mediaContent}
                            />
                        </Box>
                        <Typography variant="subtitle1" className={classes.fileName}>
                            {currentFile.name}
                        </Typography>
                    </>
                );
            } else if (currentFile.type.startsWith('video')) {
                return (
                    <>
                        <Box className={classes.mediaContainer}>
                            <video
                                src={URL.createObjectURL(currentFile)}
                                controls
                                volume={localStorage.getItem("volume")}
                                className={classes.mediaContent}
                            />
                        </Box>
                        <Typography variant="subtitle1" className={classes.fileName}>
                            {currentFile.name}
                        </Typography>
                    </>
                );
            } else if (currentFile.type.startsWith('audio')) {
                return (
                    <>
                        <Box className={classes.mediaContainer}>
                            <AudioModal url={URL.createObjectURL(currentFile)} />
                        </Box>
                        <Typography variant="subtitle1" className={classes.fileName}>
                            {currentFile.name}
                        </Typography>
                    </>
                );
            } else if (currentFile.type === 'application/pdf' || currentFile.name?.toLowerCase().endsWith('.pdf')) {
                // Miniatura/visualização do PDF via iframe (navegador renderiza nativamente)
                const pdfUrl = URL.createObjectURL(currentFile);
                return (
                    <>
                        <Box className={classes.mediaContainer}>
                            <iframe
                                title={currentFile.name}
                                src={`${pdfUrl}#toolbar=0`}
                                className={classes.mediaContent}
                                style={{ width: '100%', height: '100%', border: 'none' }}
                            />
                        </Box>
                        <Typography variant="subtitle1" className={classes.fileName}>
                            {currentFile.name}
                        </Typography>
                    </>
                );
            } else {
                // Outros tipos de arquivo (documentos, etc.) sem visualização específica
                return (
                    <>
                        <Box className={classes.mediaContainer}>
                            <CardContent>
                                <Typography variant="h6" color="textSecondary" align="center">
                                    Visualização não disponível
                                </Typography>
                                <Typography variant="body2" color="textSecondary" align="center">
                                    {currentFile.type}
                                </Typography>
                            </CardContent>
                        </Box>
                        <Typography variant="subtitle1" className={classes.fileName}>
                            {currentFile.name}
                        </Typography>
                    </>
                );
            }
        }
        return null;
    }, [currentIndex, firstTyping]);

    const handleKeyDown = (event) => {
        // ⚠️ listinha do @: com a lista aberta, Enter ESCOLHE em vez de enviar a midia.
        if (mostrarLista && listaFiltrada.length > 0) {
            if (event.key === 'ArrowDown') {
                event.preventDefault();
                setIndiceSel((i) => (i + 1) % listaFiltrada.length);
                return;
            }
            if (event.key === 'ArrowUp') {
                event.preventDefault();
                setIndiceSel((i) => (i - 1 + listaFiltrada.length) % listaFiltrada.length);
                return;
            }
            if (event.key === 'Enter' || event.key === 'Tab') {
                event.preventDefault();
                escolherMencao(listaFiltrada[indiceSel] || listaFiltrada[0]);
                return;
            }
            if (event.key === 'Escape') {
                event.preventDefault();
                setMostrarLista(false);
                return;
            }
        }
        if (event.key === 'Enter' && event.shiftKey) {
            return;
        }
        switch (event.key) {
            case 'Escape':
                onCancelSelection();
                break;
            case 'Enter':
                handleSend();
                break;
            case 'ArrowRight':
                handleNext();
                break;
            case 'ArrowLeft':
                handlePrev();
                break;
            default:
                break;
        }
    };

    return (
        <Dialog
            open={isOpen}
            fullWidth
            maxWidth="md"
            onClose={handleClose}
            classes={{ paper: classes.dialogPaper }}
            PaperProps={{ style: { maxWidth: 720 } }}
        >
            <Card className={classes.card}>
                {renderFileContent}
                
                <Divider />
                
                <Box className={classes.actions}>
                    <Box className={classes.captionWrapper}>
                        {/* ⚠️ listinha do @ (09/09/2026): sugestoes de quem marcar */}
                        {mostrarLista && listaFiltrada.length > 0 && (
                            <Box className={classes.listaMencao}>
                                {listaFiltrada.map((p, i) => (
                                    <Box
                                        key={p.jid || i}
                                        className={i === indiceSel ? classes.itemMencaoAtivo : classes.itemMencao}
                                        onMouseDown={(ev) => {
                                            ev.preventDefault();
                                            escolherMencao(p);
                                        }}
                                        onMouseEnter={() => setIndiceSel(i)}
                                    >
                                        <Typography variant="body2" noWrap>{p.name}</Typography>
                                    </Box>
                                ))}
                            </Box>
                        )}
                        <Box className={classes.captionInput}>
                            <TextField
                                fullWidth
                                variant="outlined"
                                size="small"
                                placeholder="Adicione uma legenda (opcional)"
                                multiline
                                rows={1}
                                rowsMax={2}
                                value={captions[currentIndex]}
                                onChange={handleCaptionChange}
                                onBlur={handleTextFieldBlur}
                                autoFocus
                                onKeyDown={handleKeyDown}
                            />
                        </Box>
                    </Box>
                    <Box display="flex" alignItems="center" flexShrink={0}>
                        <IconButton size="small" onClick={onCancelSelection} className={classes.navigationButton} title="Cancelar e remover anexos">
                            <Cancel />
                        </IconButton>
                    </Box>
                    <Box display="flex" alignItems="center" flexShrink={0}>
                        <IconButton 
                            size="small"
                            onClick={handlePrev} 
                            disabled={currentIndex === 0}
                            className={currentIndex === 0 ? classes.disabledButton : classes.navigationButton}
                        >
                            <NavigateBefore />
                        </IconButton>
                        <Typography variant="body2" className={classes.paginationText}>
                            {currentIndex + 1} / {files.length}
                        </Typography>
                        <IconButton 
                            size="small"
                            onClick={handleNext} 
                            disabled={currentIndex === files.length - 1}
                            className={currentIndex === files.length - 1 ? classes.disabledButton : classes.navigationButton}
                        >
                            <NavigateNext />
                        </IconButton>
                    </Box>
                    <Box display="flex" alignItems="center" flexShrink={0}>
                        <IconButton size="small" onClick={handleSend} className={classes.activeButton}>
                            <Send />
                        </IconButton>
                    </Box>
                </Box>
            </Card>
        </Dialog>
    );
};

export default MessageUploadMedias;